import type { PatternConfigEntryList } from '../../lib/pattern-config';
import {
  SCALE_ROOT_COLOR,
  SCALE_TONE_COLOR,
  type FifthInterval,
  type FourthInterval,
  type SecondInterval,
  type SeventhInterval,
  type SixthInterval,
  type ThirdInterval,
} from './common';
import {
  makeCaged_AGPosition,
  makeCaged_APosition,
  makeCaged_CPosition,
  makeCaged_DPosition,
  makeCaged_EDPosition,
  makeCaged_EPosition,
  makeCaged_GPosition,
} from './common-builders/caged';

function makeHeptatonic(
  displayName: string,
  secondInterval: SecondInterval,
  thirdInterval: ThirdInterval,
  fourthInterval: FourthInterval,
  fifthInterval: FifthInterval,
  sixthInterval: SixthInterval,
  seventhInterval: SeventhInterval,
) {
  const cagedOptions = {
    secondInterval,
    thirdInterval,
    fourthInterval,
    fifthInterval,
    sixthInterval,
    seventhInterval,
    rootColor: SCALE_ROOT_COLOR,
    toneColor: SCALE_TONE_COLOR,
  } as const;

  return {
    type: 'sublist',
    displayName,
    entries: {
      full: {
        type: 'pattern',
        displayName: 'Full',
        rules: [
          { condition: { interval: '1' }, color: SCALE_ROOT_COLOR },
          {
            condition: {
              interval: [
                secondInterval,
                thirdInterval,
                fourthInterval,
                fifthInterval,
                sixthInterval,
                seventhInterval,
              ],
            },
            color: SCALE_TONE_COLOR,
          },
        ],
        isFullOctave: true,
      },
      'caged-positions': {
        type: 'optgroup',
        displayName: 'CAGED positions',
        entries: {
          e: {
            type: 'pattern',
            displayName: 'E position',
            rules: makeCaged_EPosition(cagedOptions),
          },
          ed: {
            type: 'pattern',
            displayName: 'E-D position',
            rules: makeCaged_EDPosition(cagedOptions),
          },
          d: {
            type: 'pattern',
            displayName: 'D position',
            rules: makeCaged_DPosition(cagedOptions),
          },
          c: {
            type: 'pattern',
            displayName: 'C position',
            rules: makeCaged_CPosition(cagedOptions),
          },
          a: {
            type: 'pattern',
            displayName: 'A position',
            rules: makeCaged_APosition(cagedOptions),
          },
          ag: {
            type: 'pattern',
            displayName: 'A-G position',
            rules: makeCaged_AGPosition(cagedOptions),
          },
          g: {
            type: 'pattern',
            displayName: 'G position',
            rules: makeCaged_GPosition(cagedOptions),
          },
        },
      },
    },
  } as const satisfies PatternConfigEntryList;
}

export const PATTERNS_HEPTATONIC = {
  type: 'sublist',
  displayName: 'Heptatonic',
  entries: {
    major: makeHeptatonic('Major', '2', '3', '4', '5', '6', '7'),
    minor: makeHeptatonic('Natural minor', '2', 'b3', '4', '5', 'b6', 'b7'),
    'melodic-minor': makeHeptatonic(
      'Melodic minor (ascending)',
      '2',
      'b3',
      '4',
      '5',
      '6',
      '7',
    ),
    'harmonic-minor': makeHeptatonic(
      'Harmonic minor',
      '2',
      'b3',
      '4',
      '5',
      'b6',
      '7',
    ),
    'major-modes': {
      type: 'optgroup',
      displayName: 'Major modes',
      entries: {
        ionian: makeHeptatonic('Ionian (Major)', '2', '3', '4', '5', '6', '7'),
        dorian: makeHeptatonic('Dorian', '2', 'b3', '4', '5', '6', 'b7'),
        phrygian: makeHeptatonic('Phrygian', 'b2', 'b3', '4', '5', 'b6', 'b7'),
        lydian: makeHeptatonic('Lydian', '2', '3', '#4', '5', '6', '7'),
        mixolydian: makeHeptatonic('Mixolydian', '2', '3', '4', '5', '6', 'b7'),
        aeolian: makeHeptatonic(
          'Aeolian (Natural minor)',
          '2',
          'b3',
          '4',
          '5',
          'b6',
          'b7',
        ),
        locrian: makeHeptatonic('Locrian', 'b2', 'b3', '4', 'b5', 'b6', 'b7'),
      },
    },
    'melodic-minor-modes': {
      type: 'optgroup',
      displayName: 'Melodic minor modes',
      entries: {
        'melodic-minor': makeHeptatonic(
          'Ionian ♭3 (Melodic minor)',
          '2',
          'b3',
          '4',
          '5',
          '6',
          '7',
        ),
        'dorian-b2': makeHeptatonic(
          'Dorian ♭2',
          'b2',
          'b3',
          '4',
          '5',
          '6',
          'b7',
        ),
        'lydian-sharp-5': makeHeptatonic(
          'Lydian ♯5',
          '2',
          '3',
          '#4',
          '#5',
          '6',
          '7',
        ),
        'lydian-b7': makeHeptatonic(
          'Lydian ♭7',
          '2',
          '3',
          '#4',
          '5',
          '6',
          'b7',
        ),
        'mixolydian-b6': makeHeptatonic(
          'Mixolydian ♭6',
          '2',
          '3',
          '4',
          '5',
          'b6',
          'b7',
        ),
        'locrian-natural-2': makeHeptatonic(
          'Locrian ♮2',
          '2',
          'b3',
          '4',
          'b5',
          'b6',
          'b7',
        ),
        altered: makeHeptatonic('Altered', 'b2', 'b3', 'b4', 'b5', 'b6', 'bb7'),
      },
    },
    'harmonic-minor-modes': {
      type: 'optgroup',
      displayName: 'Harmonic minor modes',
      entries: {
        'harmonic-minor': makeHeptatonic(
          'Aeolian ♯7 (Harmonic minor)',
          '2',
          'b3',
          '4',
          '5',
          'b6',
          '7',
        ),
        'locrian-natural-6': makeHeptatonic(
          'Locrian ♮6',
          'b2',
          'b3',
          '4',
          'b5',
          '6',
          'b7',
        ),
        'ionian-sharp-5': makeHeptatonic(
          'Ionian ♯5',
          '2',
          '3',
          '4',
          '#5',
          '6',
          '7',
        ),
        'dorian-sharp-4': makeHeptatonic(
          'Dorian ♯4',
          '2',
          'b3',
          '#4',
          '5',
          '6',
          'b7',
        ),
        'phrygian-dominant': makeHeptatonic(
          'Phrygian Dominant',
          'b2',
          '3',
          '4',
          '5',
          'b6',
          'b7',
        ),
        'lydian-sharp-2': makeHeptatonic(
          'Lydian ♯2',
          '#2',
          '3',
          '#4',
          '5',
          '6',
          '7',
        ),
        'super-locrian-bb7': makeHeptatonic(
          'Super Locrian ♭♭7',
          'b2',
          'b3',
          'b4',
          'b5',
          'b6',
          'bb7',
        ),
      },
    },
  },
} as const satisfies PatternConfigEntryList;
